import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, CreditCard, LogOut, ShieldCheck } from "lucide-react";
import { useAuthStore } from "../state/authStore";

function initials(name) {
    if (!name) return "?";
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((s) => s[0])
        .join("")
        .toUpperCase();
}

export const UserMenu = () => {
    const { user, logout } = useAuthStore();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    if (!user) return null;

    const planLabel = (user.subscription_plan || "free").toUpperCase();
    const planTone =
        user.subscription_plan === "msp"
            ? "text-accent-mint"
            : user.subscription_plan === "pro"
                ? "text-accent-cyan"
                : "text-text-dim";

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2.5 group focus:outline-none"
                data-testid="user-menu-trigger"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                {user.picture ? (
                    <img
                        src={user.picture}
                        alt=""
                        className="h-8 w-8 rounded-full border border-white/[0.1] object-cover"
                        draggable={false}
                    />
                ) : (
                    <span
                        className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-accent-cyan/15 border border-accent-cyan/40 text-[12px] font-semibold text-accent-cyan"
                        data-testid="user-menu-initials"
                    >
                        {initials(user.name || user.email)}
                    </span>
                )}
                <span
                    className={`hidden md:inline text-[11px] font-mono uppercase tracking-[0.18em] ${planTone}`}
                    data-testid="user-menu-plan"
                >
                    {planLabel}
                </span>
            </button>

            {open && (
                <div
                    role="menu"
                    className="absolute right-0 mt-2 w-56 rounded-xl border border-white/[0.08] bg-surface/95 backdrop-blur-md shadow-heavy-popover overflow-hidden z-50"
                    data-testid="user-menu-dropdown"
                >
                    <div className="px-4 py-3 border-b border-white/[0.05]">
                        <p className="text-[13px] font-semibold text-text-primary truncate">
                            {user.name || "Account"}
                        </p>
                        <p className="text-[11.5px] text-text-muted truncate">
                            {user.email}
                        </p>
                    </div>
                    <Link
                        to="/dashboard"
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-text-muted hover:text-text-primary hover:bg-white/[0.04] transition-colors duration-150"
                        data-testid="user-menu-dashboard"
                    >
                        <LayoutDashboard className="w-3.5 h-3.5" />
                        Dashboard
                    </Link>
                    <Link
                        to="/pricing"
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-text-muted hover:text-text-primary hover:bg-white/[0.04] transition-colors duration-150"
                        data-testid="user-menu-subscription"
                    >
                        <CreditCard className="w-3.5 h-3.5" />
                        Subscription
                    </Link>
                    {user.role === "admin" && (
                        <div className="flex items-center gap-2.5 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.2em] text-accent-mint border-t border-white/[0.04]">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            Admin
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={async () => {
                            await logout();
                            setOpen(false);
                            navigate("/");
                        }}
                        className="w-full text-left flex items-center gap-2.5 px-4 py-2.5 text-[13px] text-accent-red hover:bg-accent-red/[0.05] transition-colors duration-150 border-t border-white/[0.04]"
                        data-testid="user-menu-logout"
                    >
                        <LogOut className="w-3.5 h-3.5" />
                        Sign out
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserMenu;
